import { z } from 'zod';
import { WidgetSpec, isLegacyWidget, toWidgetSpec } from './widgets';

/**
 * Boards (copilot chat v2 plan PART 4): a board is an evolving DOCUMENT of blocks —
 * prose, live app panels, computed widgets — streamed as zod-validated JSON OPS
 * (`data-board` parts), one per op, and reduced to a document by {@link applyBoardOps}.
 * NOT MDX: ops are validatable, stream cleanly, and let turn 5 rewrite turn 2's block.
 *
 * ZOD-3 COMPATIBILITY (same rule as widgets.ts): apps/web bundles this SOURCE with zod v3
 * while the backend runs v4 — only syntax valid in BOTH may be used here.
 *
 * The wire discipline: an op that fails its schema is DROPPED (valid siblings survive —
 * the collect-widgets precedent); the reducer itself is TOTAL over valid ops and never
 * throws. Board/block ids are composer-authored short slugs so later ops can target them.
 */

/** Composer-authored id: short, lowercase, url/log-safe. Never repurposed once persisted. */
const slugRe = /^[a-z0-9][a-z0-9_-]{0,39}$/;
export const BoardSlug = z.string().regex(slugRe);

/**
 * The CLOSED interaction vocabulary (plan §4.3) — no arbitrary handlers:
 * - `ask` sends a prompt back into the chat ("ask further" chips, what-if buttons);
 * - `open-in-app` deep-links to the panel's home route (route comes from the FE catalog
 *   binding, never from the composer);
 * - `set-param` re-hydrates a catalog block with new props client-side (no model turn);
 *   the legal keys are the catalog entry's `adjustable` list, enforced at render.
 */
export const BoardActionSchema = z.union([
  z.object({
    kind: z.literal('ask'),
    label: z.string().min(1).max(60),
    prompt: z.string().min(1).max(500),
  }),
  z.object({
    kind: z.literal('open-in-app'),
    label: z.string().min(1).max(60).optional(),
  }),
  z.object({
    kind: z.literal('set-param'),
    label: z.string().min(1).max(60),
    params: z.record(z.string(), z.unknown()),
  }),
]);
export type BoardAction = z.infer<typeof BoardActionSchema>;

/**
 * The block — the atom of a board (plan §4.2):
 * - `text`: markdown prose (headings, verdicts, annotations);
 * - `catalog`: a REAL app panel by catalog id; carries PARAMS, never data (§4.3) — the
 *   client hydrates through the TanStack spine. `props` stays `unknown` here; the
 *   backend validates it against `BOARD_CATALOG[panel].props` before persisting, and the
 *   FE re-parses at render (stale props degrade per-block, never board-fatal);
 * - `widget`: model-COMPUTED content via the EXISTING shared widget union (already
 *   zod-gated, already rendering, already capped) — no third spec language. WRITTEN as the
 *   canonical `{ type, props }`; a PERSISTED block may hold the legacy flat widget, which
 *   `parseBoardOps` / `applyBoardOps` canonicalize on read (see `canonicalWidgetBlock`).
 */
export const BlockSpecSchema = z.union([
  z.object({
    kind: z.literal('text'),
    md: z.string().min(1).max(8000),
  }),
  z.object({
    kind: z.literal('catalog'),
    panel: BoardSlug,
    props: z.unknown(),
    actions: z.array(BoardActionSchema).max(3).optional(),
  }),
  z.object({
    kind: z.literal('widget'),
    widget: WidgetSpec,
    actions: z.array(BoardActionSchema).max(3).optional(),
  }),
]);
export type BlockSpec = z.infer<typeof BlockSpecSchema>;

/** One streamed board operation — the `data-board` part payload. */
export const BoardOpSchema = z.union([
  z.object({
    op: z.literal('create'),
    boardId: BoardSlug,
    title: z.string().min(1).max(120),
  }),
  z.object({
    op: z.literal('add_block'),
    boardId: BoardSlug,
    blockId: BoardSlug,
    block: BlockSpecSchema,
    /** Insertion index (clamped to the document); absent = append. */
    position: z.number().int().min(0).optional(),
  }),
  z.object({
    op: z.literal('update_block'),
    boardId: BoardSlug,
    blockId: BoardSlug,
    /** FULL replacement — no deep merge (deterministic, no merge ambiguity). */
    block: BlockSpecSchema,
  }),
  z.object({
    op: z.literal('remove_block'),
    boardId: BoardSlug,
    blockId: BoardSlug,
  }),
]);
export type BoardOp = z.infer<typeof BoardOpSchema>;

/**
 * A reduced board document — what `chat_board_versions.doc` stores and the FE renders. Versions
 * written before `{ type, props }` hold legacy flat widgets; `applyBoardOps` canonicalizes the
 * `doc` it is handed, and a renderer reading a stored doc directly goes through `toWidgetSpec`.
 */
export interface BoardDoc {
  boardId: string;
  title: string;
  blocks: Array<{ id: string; block: BlockSpec }>;
}

/**
 * THE reducer (plan §4.5): pure, deterministic, total over schema-valid ops — used LIVE by
 * the FE as ops stream in, and by the backend to materialize `chat_board_versions`, so the
 * two can never disagree about what a board looks like. Semantics chosen for
 * never-lose-content forgiveness (the composer is a model):
 * - ops addressed to a different `boardId` are ignored;
 * - `create` on a fresh id starts an empty document; on an existing one it only re-titles
 *   (blocks survive — removal is `remove_block`'s job, never a side effect);
 * - `add_block` with an EXISTING blockId replaces that block in place (id is identity);
 *   `position` is clamped into range, absent = append;
 * - `update_block` on a missing id APPENDS instead (an earlier op may have been dropped by
 *   validation — content beats strictness);
 * - `remove_block` on a missing id is a no-op;
 * - every op before the board's `create` is buffered semantics-free: a block op with no
 *   document yet CREATES an untitled document (the create may arrive late in a hand-written
 *   or partially-dropped stream).
 * Never mutates its inputs.
 */
export function applyBoardOps(doc: BoardDoc | null, ops: readonly BoardOp[]): BoardDoc | null {
  let out = doc
    ? {
        ...doc,
        blocks: doc.blocks.map((b) => ({ id: b.id, block: canonicalWidgetBlock(b.block) })),
      }
    : null;
  for (const op of ops) {
    if (out && op.boardId !== out.boardId) continue;
    out = applyBoardOp(out, op);
  }
  return out;
}

type OpOf<K extends BoardOp['op']> = Extract<BoardOp, { op: K }>;

/** A block op with no document yet creates an untitled one (the `create` may arrive late). */
const untitledDoc = (boardId: string): BoardDoc => ({ boardId, title: '', blocks: [] });

/** One op on a document `applyBoardOps` owns (its `blocks` array is already a private copy). */
function applyBoardOp(out: BoardDoc | null, op: BoardOp): BoardDoc | null {
  switch (op.op) {
    case 'create':
      return out
        ? { ...out, title: op.title }
        : { boardId: op.boardId, title: op.title, blocks: [] };
    case 'add_block':
      return addBlock(out ?? untitledDoc(op.boardId), op);
    case 'update_block':
      return updateBlock(out ?? untitledDoc(op.boardId), op);
    case 'remove_block':
      return out && { ...out, blocks: out.blocks.filter((b) => b.id !== op.blockId) };
  }
}

/** An existing blockId is replaced in place; otherwise inserted at `position`, clamped (absent = append). */
function addBlock(doc: BoardDoc, op: OpOf<'add_block'>): BoardDoc {
  const entry = { id: op.blockId, block: op.block };
  const existing = doc.blocks.findIndex((b) => b.id === op.blockId);
  if (existing >= 0) {
    doc.blocks[existing] = entry;
  } else {
    doc.blocks.splice(Math.min(op.position ?? doc.blocks.length, doc.blocks.length), 0, entry);
  }
  return doc;
}

/** Replaces the block; a missing id APPENDS (an earlier op may have been dropped by validation). */
function updateBlock(doc: BoardDoc, op: OpOf<'update_block'>): BoardDoc {
  const entry = { id: op.blockId, block: op.block };
  const idx = doc.blocks.findIndex((b) => b.id === op.blockId);
  if (idx >= 0) {
    doc.blocks[idx] = entry;
  } else {
    doc.blocks.push(entry);
  }
  return doc;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A widget block whose widget is the LEGACY flat shape, rewritten to the canonical
 * `{ type, props }` through `toWidgetSpec` (the one adapter, which also heals it); every other
 * block — and a legacy widget even the healer refuses — is returned as-is.
 *
 * THE FAILURE THIS PREVENTS: 1,031 stored board versions and every `data-board` part written
 * before `{ type, props }` carry flat widgets, and nothing migrates them. Validated against the
 * canonical `BlockSpecSchema` alone, each such op is DROPPED and an old board replays without its
 * widgets; reduced on top of an old doc, a new version would persist the flat shape forever. This
 * is a normalize step BEFORE the schema, not a `z.preprocess` inside it: the schema stays pure
 * zod both majors run (apps/web bundles zod 3), and the WRITE path (`BoardOpSchema` on the
 * composer's live ops) stays strict — only reads convert.
 */
function canonicalWidgetBlock<B>(block: B): B {
  if (!isRecord(block) || block.kind !== 'widget' || !isLegacyWidget(block.widget)) return block;
  const widget = toWidgetSpec(block.widget);
  return widget ? { ...block, widget } : block;
}

/** An op whose `block` carries a legacy widget, canonicalized; anything else untouched. */
function canonicalOp(raw: unknown): unknown {
  if (!isRecord(raw) || !isRecord(raw.block)) return raw;
  const block = canonicalWidgetBlock(raw.block);
  return block === raw.block ? raw : { ...raw, block };
}

/**
 * Parse an untrusted op stream: schema-valid ops survive, invalid ones are dropped and
 * COUNTED (the `board.op.drop` telemetry input) — the collect-widgets posture. A PERSISTED op
 * with a legacy flat widget is canonicalized first (`canonicalWidgetBlock`), so it replays.
 */
export function parseBoardOps(raw: readonly unknown[]): { ops: BoardOp[]; dropped: number } {
  const ops: BoardOp[] = [];
  let dropped = 0;
  for (const candidate of raw) {
    const r = BoardOpSchema.safeParse(canonicalOp(candidate));
    if (r.success) ops.push(r.data);
    else dropped += 1;
  }
  return { ops, dropped };
}
